import { BodyObjectUpdate, CarCard, CarCardAttachment, CardsService, CarObjectUpdate, FaceObjectUpdate, HumanCard, HumanCardAttachment } from '@/api';
import { actionHandler } from '@/store/data/ActionHandler';
import { ItemsActionNames } from '@/definitions/app/item.actions.name';
import { MultisidebarItemType, MultisidebarItemTypes } from '@/store/multisidebar/types';
import { multisidebarModule } from '@/store/multisidebar';
import { generateMultisidebarId } from '@/store/multisidebar/helpers';
import { shortString } from '@/common/filters';
import ResolveOutsidePromise from '@/common/ResolveOutsidePromise';
import { ListViewModel } from '@/definitions/view-models';
import { loadDataModule } from '@/store/data/LoadDataModule';
import { viewModelRepository } from '@/api/common';

/*

example:

matchedCardHelper = new MatchedCardHelper();
created() {
  this.matchedCardHelper.isLoadObject = true;
  this.matchedCardHelper.isLoadAttachments = true;
  this.matchedCardHelper.type = this.objectType;
  this.matchedCardHelper.cardId = this.id;
}

*/

export default class MatchedCardHelper {
  type: string | undefined = '';
  card: HumanCard | CarCard | null = null;

  isLoadObject = false;
  object: FaceObjectUpdate | BodyObjectUpdate | CarObjectUpdate | null = null;
  forceObjectId: number | string | null = null;

  isLoadAttachments = false;
  attachments: HumanCardAttachment[] | CarCardAttachment[] = [];

  loading = false;
  loadingPromise = new ResolveOutsidePromise();

  private _cardId: number | null = null;

  get cardId(): number | null {
    return this._cardId;
  }

  set cardId(id: number | null) {
    this._cardId = id;
    this.loadCard();
  }

  get objectType() {
    if (this.type === 'face' || this.type === 'faces' || this.type === 'humans') {
      return 'faces';
    }
    if (this.type === 'body' || this.type === 'bodies') {
      return 'bodies';
    }
    if (this.type === 'car' || this.type === 'cars') {
      return 'cars';
    }
    return '';
  }

  get isCars() {
    return this.objectType === 'cars';
  }

  async loadCard() {
    this.card = null;
    this.object = null;
    this.attachments = [];

    if (!this._cardId) {
      return;
    }

    this.loading = true;
    this.loadingPromise = new ResolveOutsidePromise();

    const servicePromise = this.isCars ? CardsService.cardsCarsRetrieve(this._cardId) : CardsService.cardsHumansRetrieve(this._cardId);
    try {
      this.card = await (servicePromise as Promise<HumanCard | CarCard>);
    } catch (e) {
      console.warn('Load card error', e);
    }

    this.loading = false;
    this.loadingPromise.resolve();

    if (!this.card) {
      // Нет смысла грузить объекты и аттачменты если карточки нет
      // Если грузить аттачменты удалённой карточки будет ошибка
      return;
    }

    // Load card object
    if (this.isLoadObject) {
      this.loadObject();
    }

    // Load card attachments
    if (this.isLoadAttachments) {
      this.loadAttachments();
    }
  }

  async loadObject() {
    try {
      let objectVM: ListViewModel<any, any> | null = null;
      if (this.forceObjectId !== null) {
        objectVM = loadDataModule.getObjectsLVMByID({ id: this.forceObjectId, type: this.objectType, limit: 1 });
      } else {
        objectVM = loadDataModule.getObjectsLVMByCardID({ id: this._cardId as any, type: this.objectType, limit: 1 });
      }

      await objectVM.firstLoadingPromise;

      if (objectVM.items.length) {
        this.object = objectVM.items[0] as any;
      }
    } catch (e) {
      console.warn('Load object error', e);
    }
  }

  async loadAttachments() {
    try {
      const model = this.isCars ? viewModelRepository.getCarCardAttachmentsListViewModel() : viewModelRepository.getHumanCardAttachmentsListViewModel();
      model.filter.current.card = this._cardId || undefined;
      model.filter.current.limit = 100;
      await model.get();
      this.attachments = model.items || [];
    } catch (e) {
      console.warn('Load attachments error', e);
    }
  }

  showCard() {
    if (!this.card) {
      console.warn('Error, card is null');
      return;
    }
    actionHandler.run(ItemsActionNames.ShowItem, {
      type: this.isCars ? MultisidebarItemTypes.CardsCars : MultisidebarItemTypes.CardsHumans,
      rawItem: this.card
    });
  }

  showFullscreen() {
    if (this.object) {
      actionHandler.run(ItemsActionNames.ShowFullScreen, this.object);
    }
  }

  get pageSidebarType() {
    return `cards__${this.type}` as MultisidebarItemType;
  }

  get isSelected() {
    if (this.card && this.card.id) {
      return !!multisidebarModule.getItem(this.pageSidebarType, this.card.id);
    }
    return false;
  }

  get isOpened() {
    if (this.card && this.card.id) {
      return multisidebarModule.currentItem?.id === generateMultisidebarId(this.pageSidebarType, this.card.id);
    }
    return false;
  }

  get name() {
    return this.card ? this.card.name : '';
  }

  get formattedName() {
    return shortString(this.name, 90);
  }

  get comment() {
    return this.card?.comment;
  }

  get licensePlateNumber() {
    return (this.card as CarCard)?.license_plate_number;
  }

  get thumbnail(): string | undefined {
    return this.object ? this.object.thumbnail : undefined;
  }
}
